import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Loader2,
  LogOut,
  MoreHorizontal,
  PencilLine,
  Search,
  ShieldOff,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AuthApiError, useAuth } from "@/lib/auth";
import {
  forceLogoutUser,
  listUsers,
  reset2faForUser,
  sendPasswordResetForUser,
  type AdminUserRow,
  type UserRole,
  type UserStatus,
} from "@/lib/auth/admin-api";
import { formatAbsoluteDateTime, formatRelativeTime } from "@/lib/format";

import {
  ConfirmUserActionDialog,
  defaultActionErrorMessage,
} from "./confirm-user-action-dialog";
import { DeleteUserDialog } from "./delete-user-dialog";
import { EditUserDialog } from "./edit-user-dialog";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

type RoleFilter = "all" | UserRole;
type StatusFilter = "all" | UserStatus;

type ActionKind = "force-logout" | "reset-2fa" | "send-password-reset";

interface ActionState {
  kind: ActionKind;
  user: AdminUserRow;
}

interface ToastState {
  tone: "success" | "info";
  message: string;
}

function RoleBadge({ role }: { role: UserRole }) {
  return (
    <Badge variant={role === "admin" ? "default" : "outline"}>
      {role === "admin" ? "Admin" : "User"}
    </Badge>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  return (
    <Badge variant={status === "active" ? "success" : "destructive"}>
      {status === "active" ? "Active" : "Disabled"}
    </Badge>
  );
}

function TotpBadge({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <Badge variant="success">
      <CheckCircle2 className="h-3 w-3" aria-hidden />
      On
    </Badge>
  ) : (
    <Badge variant="outline">Off</Badge>
  );
}

function formatMaybeDate(iso: string | null): {
  absolute: string;
  relative: string;
} {
  if (!iso) return { absolute: "—", relative: "—" };
  return {
    absolute: formatAbsoluteDateTime(iso),
    relative: formatRelativeTime(iso),
  };
}

export function UsersPanel() {
  const { user: principal } = useAuth();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  const [editTarget, setEditTarget] = useState<AdminUserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);
  const [actionState, setActionState] = useState<ActionState | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  // Debounce the free-text search so a keystroke burst doesn't fire one
  // request per character. Filter / pagination changes apply immediately.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  // Resetting filters should always reset pagination — otherwise an offset
  // that was valid for the previous query lands on an empty page.
  useEffect(() => {
    setOffset(0);
  }, [roleFilter, statusFilter]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    listUsers({
      q: debouncedQuery || undefined,
      role: roleFilter === "all" ? undefined : roleFilter,
      status: statusFilter === "all" ? undefined : statusFilter,
      limit: PAGE_SIZE,
      offset,
      signal: controller.signal,
    })
      .then((res) => {
        setRows(res.users);
        setTotal(res.total);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof AuthApiError) {
          setLoadError(err.serverMessage ?? "Couldn't load users.");
        } else {
          setLoadError("Couldn't reach the server. Check your connection.");
        }
        setLoading(false);
      });
    return () => controller.abort();
  }, [debouncedQuery, roleFilter, statusFilter, offset, refetchKey]);

  // Auto-dismiss the inline toast so the row strip doesn't grow forever
  // after a chain of actions.
  useEffect(() => {
    if (!toast) return;
    const handle = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(handle);
  }, [toast]);

  const triggerRefetch = useCallback(() => {
    setRefetchKey((k) => k + 1);
  }, []);

  const handleEditSuccess = useCallback(
    (updated: AdminUserRow) => {
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setToast({ tone: "success", message: `Updated ${updated.email}.` });
    },
    [],
  );

  const handleDeleteSuccess = useCallback(() => {
    setToast({
      tone: "success",
      message: "User deleted. All sessions revoked.",
    });
    triggerRefetch();
  }, [triggerRefetch]);

  const actionTitle = useMemo(() => {
    if (!actionState) return "";
    switch (actionState.kind) {
      case "force-logout":
        return "Force sign out";
      case "reset-2fa":
        return "Reset 2FA";
      case "send-password-reset":
        return "Send password reset email";
    }
  }, [actionState]);

  const actionDescription = useMemo(() => {
    if (!actionState) return null;
    const email = actionState.user.email;
    switch (actionState.kind) {
      case "force-logout":
        return (
          <>
            Sign{" "}
            <span className="font-mono text-foreground">{email}</span> out of
            every device. They&apos;ll have to log in again, and any trusted
            device will need to pass 2FA on next login.
          </>
        );
      case "reset-2fa":
        return (
          <>
            Clear the TOTP enrollment for{" "}
            <span className="font-mono text-foreground">{email}</span>. They
            must re-enroll on next sign-in, and their trusted devices are
            revoked so the next sign-in requires 2FA from scratch.
          </>
        );
      case "send-password-reset":
        return (
          <>
            Email a password reset link to{" "}
            <span className="font-mono text-foreground">{email}</span>. The
            link is valid for one hour. The user&apos;s existing sessions are
            unaffected until they actually reset.
          </>
        );
    }
  }, [actionState]);

  const actionConfirmLabel = useMemo(() => {
    if (!actionState) return "Confirm";
    switch (actionState.kind) {
      case "force-logout":
        return "Sign out everywhere";
      case "reset-2fa":
        return "Reset 2FA";
      case "send-password-reset":
        return "Send reset email";
    }
  }, [actionState]);

  const actionLoadingLabel = useMemo(() => {
    if (!actionState) return "Working…";
    switch (actionState.kind) {
      case "force-logout":
        return "Signing out…";
      case "reset-2fa":
        return "Resetting…";
      case "send-password-reset":
        return "Sending…";
    }
  }, [actionState]);

  const performAction = useCallback(async () => {
    if (!actionState) return;
    switch (actionState.kind) {
      case "force-logout":
        await forceLogoutUser(actionState.user.id);
        return;
      case "reset-2fa":
        await reset2faForUser(actionState.user.id);
        return;
      case "send-password-reset":
        await sendPasswordResetForUser(actionState.user.id);
        return;
    }
  }, [actionState]);

  const onActionSuccess = useCallback(() => {
    if (!actionState) return;
    const email = actionState.user.email;
    switch (actionState.kind) {
      case "force-logout":
        setToast({
          tone: "success",
          message: `Signed ${email} out of all devices.`,
        });
        triggerRefetch();
        return;
      case "reset-2fa":
        setRows((prev) =>
          prev.map((r) =>
            r.id === actionState.user.id ? { ...r, totp_enabled: 0 } : r,
          ),
        );
        setToast({
          tone: "success",
          message: `Cleared 2FA for ${email}.`,
        });
        return;
      case "send-password-reset":
        setToast({
          tone: "success",
          message: `Password reset email sent to ${email}.`,
        });
        return;
    }
  }, [actionState, triggerRefetch]);

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, total);
  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < total;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="users-search" className="text-xs">
            Search
          </Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="users-search"
              type="search"
              placeholder="Filter by name or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 pl-8"
            />
          </div>
        </div>
        <div className="w-full sm:w-36 space-y-1.5">
          <Label htmlFor="users-role-filter" className="text-xs">
            Role
          </Label>
          <Select
            value={roleFilter}
            onValueChange={(v) => setRoleFilter(v as RoleFilter)}
          >
            <SelectTrigger id="users-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-36 space-y-1.5">
          <Label htmlFor="users-status-filter" className="text-xs">
            Status
          </Label>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as StatusFilter)}
          >
            <SelectTrigger id="users-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {toast ? (
        <p
          role="status"
          className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success"
        >
          {toast.message}
        </p>
      ) : null}

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>2FA</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-12 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Loading users…
                  </div>
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className="space-y-2 py-6 text-center">
                    <p className="text-sm text-destructive">{loadError}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={triggerRefetch}
                    >
                      Retry
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  <p className="py-6 text-sm text-muted-foreground">
                    No users match these filters.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const isSelf = principal?.id === row.id;
                const lastLogin = formatMaybeDate(row.last_login_at);
                const created = formatMaybeDate(row.created_at);
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">
                          {row.name}
                        </span>
                        {isSelf ? (
                          <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">
                            You
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.email}
                    </TableCell>
                    <TableCell>
                      <RoleBadge role={row.role} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell>
                      <TotpBadge enabled={row.totp_enabled === 1} />
                    </TableCell>
                    <TableCell
                      title={lastLogin.absolute}
                      className="text-xs text-muted-foreground"
                    >
                      {lastLogin.relative}
                    </TableCell>
                    <TableCell
                      title={created.absolute}
                      className="text-xs text-muted-foreground"
                    >
                      {created.relative}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Actions for ${row.email}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              setEditTarget(row);
                            }}
                          >
                            <PencilLine className="h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              setActionState({ kind: "force-logout", user: row });
                            }}
                          >
                            <LogOut className="h-4 w-4" />
                            Force sign out
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              setActionState({ kind: "reset-2fa", user: row });
                            }}
                          >
                            <ShieldOff className="h-4 w-4" />
                            Reset 2FA
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              setActionState({
                                kind: "send-password-reset",
                                user: row,
                              });
                            }}
                            disabled={row.status !== "active"}
                          >
                            <KeyRound className="h-4 w-4" />
                            Send password reset
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={isSelf}
                            onSelect={(e) => {
                              e.preventDefault();
                              if (isSelf) return;
                              setDeleteTarget(row);
                            }}
                            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete user
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          {loading || loadError ? (
            <>&nbsp;</>
          ) : total === 0 ? (
            "No users"
          ) : (
            <>
              Showing{" "}
              <span className="font-mono text-foreground">{showingFrom}</span>–
              <span className="font-mono text-foreground">{showingTo}</span> of{" "}
              <span className="font-mono text-foreground">{total}</span>
            </>
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={!hasPrev || loading}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!hasNext || loading}
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <EditUserDialog
        open={editTarget !== null}
        user={editTarget}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        onSuccess={handleEditSuccess}
      />

      <DeleteUserDialog
        open={deleteTarget !== null}
        user={deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onSuccess={handleDeleteSuccess}
      />

      <ConfirmUserActionDialog
        open={actionState !== null}
        title={actionTitle}
        description={actionDescription}
        confirmLabel={actionConfirmLabel}
        loadingLabel={actionLoadingLabel}
        destructive={actionState?.kind !== "send-password-reset"}
        errorMessage={defaultActionErrorMessage}
        onConfirm={performAction}
        onOpenChange={(open) => {
          if (!open) setActionState(null);
        }}
        onSuccess={onActionSuccess}
      />
    </div>
  );
}
